import { ChangeEvent, CSSProperties, SubmitEvent, useState } from "react"
import { PageComponent, Text, Button, Input } from "../../components"
import './login.css'
import { API_BASE_URL } from '../../config'
import { getRateLimitMessage } from '../../helpers'
import { startMailVerification } from '../verify-email'

type Mode = 'login' | 'register'

interface LoginFormState {
    login: string;
    password: string;
}

interface RegisterFormState {
    mail: string;
    login: string;
    password: string;
    confirmPassword: string;
}

const INITIAL_LOGIN: LoginFormState = {login: '', password: ''}
const INITIAL_REGISTER: RegisterFormState = {mail: '', login: '', password: '', confirmPassword: ''}

// Документы по 152-ФЗ: политика и согласие должны быть отдельными документами
const PRIVACY_POLICY_URL = '/privacy'
const PD_CONSENT_URL = '/consent'
const PD_DISTRIBUTION_CONSENT_URL = '/consent-distribution'
const TERMS_URL = '/terms'

// ошибки бэка на английском переводим, русские показываем как есть
const REGISTER_ERRORS: Record<string, string> = {
    'Login already taken': 'Этот логин уже занят',
    'Personal data consent required': 'Необходимо согласие на обработку персональных данных',
    'Personal data distribution consent required': 'Необходимо согласие на распространение персональных данных',
}

const getErrorDetail = async (response: Response): Promise<string | null> => {
    try {
        const data = await response.json()
        return typeof data?.detail === 'string' ? data.detail : null
    } catch {
        return null
    }
}


const getCardFromPath = (): string | null => {
    const match = window.location.pathname.match(/^\/admin\/([^/]+)/)
    return match ? decodeURIComponent(match[1]) : null
}

const themeVars = {
    '--lio-text': '#f9f9f9',
    '--lio-input-bg': 'rgba(255, 255, 255, 0.05)',
} as unknown as CSSProperties

interface GetQrAdminLoginPageProps {
    onAuthenticated?: (token: string) => void;
}

export const GetQrAdminLoginPage = ({onAuthenticated}: GetQrAdminLoginPageProps = {}) => {
    const [mode, setMode] = useState<Mode>('login')
    const [loginForm, setLoginForm] = useState<LoginFormState>(INITIAL_LOGIN)
    const [registerForm, setRegisterForm] = useState<RegisterFormState>(INITIAL_REGISTER)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [pdConsent, setPdConsent] = useState(false)
    const [pdDistributionConsent, setPdDistributionConsent] = useState(false)

    const switchMode = (nextMode: Mode) => {
        setMode(nextMode)
        setError(null)
    }

    const updateLoginField = (field: keyof LoginFormState) => (e: ChangeEvent<HTMLInputElement>) => {
        setLoginForm((prev) => ({...prev, [field]: e.target.value}))
    }

    const updateRegisterField = (field: keyof RegisterFormState) => (e: ChangeEvent<HTMLInputElement>) => {
        setRegisterForm((prev) => ({...prev, [field]: e.target.value}))
    }

    const handleSubmit = async (e: SubmitEvent) => {
        e.preventDefault()
        setError(null)

        if (mode === 'login') {
            if (!loginForm.login || !loginForm.password) {
                setError('Заполните логин и пароль')
                return
            }
        } else {
            if (!registerForm.login || !registerForm.password) {
                setError('Заполните логин и пароль')
                return
            }
            if (registerForm.password !== registerForm.confirmPassword) {
                setError('Пароли не совпадают')
                return
            }
            if (!pdConsent) {
                setError('Необходимо согласие на обработку персональных данных')
                return
            }
            if (!pdDistributionConsent) {
                setError('Необходимо согласие на распространение персональных данных')
                return
            }
        }

        setLoading(true)
        try {
            const card = getCardFromPath()
            const query = card ? `?card=${encodeURIComponent(card)}` : ''
            const response = await fetch(`${API_BASE_URL}/${mode}${query}`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(
                    mode === 'login'
                        ? {login: loginForm.login, password: loginForm.password}
                        : {mail: registerForm.mail || undefined, login: registerForm.login, password: registerForm.password, pd_consent: pdConsent, pd_distribution_consent: pdDistributionConsent}
                ),
            })

            if (!response.ok) {
                const rateLimit = getRateLimitMessage(response)
                if (rateLimit) {
                    setError(rateLimit)
                    return
                }
                const detail = mode === 'register' ? await getErrorDetail(response) : null
                if (detail && detail !== 'Invalid card') {
                    setError(REGISTER_ERRORS[detail] ?? detail)
                    return
                }
                throw new Error('Request failed')
            }

            const data = await response.json()
            onAuthenticated?.(data.access_token)
            if (mode === 'register' && registerForm.mail) {
                startMailVerification(registerForm.mail)
            }
        } catch {
            setError(mode === 'login' ? 'Неверный логин или пароль' : getCardFromPath() ? 'Не удалось зарегистрироваться' : 'Пожалуйста, приобретите карту для регистрации или отсканируйте уже имеющуюся')
        } finally {
            setLoading(false)
        }
    }

    return (
        <PageComponent>
            <div className="admin-login-card" style={themeVars}>
                <Text size="l" color="white">Админ-панель</Text>

                <div className="admin-login-tabs">
                    <div className={`admin-login-tab${mode === 'login' ? ' admin-login-tab-active' : ''}`}>
                        <Button variant="ghost" fullWidth textSize="s" textColor={mode === 'login' ? 'white' : 'lightGray'} onClick={() => switchMode('login')}>Вход</Button>
                    </div>
                    <div className={`admin-login-tab${mode === 'register' ? ' admin-login-tab-active' : ''}`}>
                        <Button variant="ghost" fullWidth textSize="s" textColor={mode === 'register' ? 'white' : 'lightGray'} onClick={() => switchMode('register')}>Регистрация</Button>
                    </div>
                </div>

                <form className="admin-login-form" onSubmit={handleSubmit}>
                    {mode === 'login' ? (
                        <>
                            <Input label="Логин" placeholder="Ваш логин" autoComplete="username" value={loginForm.login} onChange={updateLoginField('login')} />
                            <Input label="Пароль" placeholder="Пароль" secureToggle autoComplete="current-password" value={loginForm.password} onChange={updateLoginField('password')} />
                        </>
                    ) : (
                        <>
                            <Input label="Почта (необязательно)" type="email" placeholder="you@example.com" autoComplete="email" value={registerForm.mail} onChange={updateRegisterField('mail')} />
                            <Input label="Логин" placeholder="Придумайте логин" autoComplete="username" value={registerForm.login} onChange={updateRegisterField('login')} />
                            <Input label="Пароль" placeholder="Минимум 6 символов" secureToggle autoComplete="new-password" value={registerForm.password} onChange={updateRegisterField('password')} />
                            <Input label="Повтор пароля" placeholder="Повторите пароль" secureToggle autoComplete="new-password" value={registerForm.confirmPassword} onChange={updateRegisterField('confirmPassword')} />
                            <label className="admin-login-consent jost">
                                <input type="checkbox" checked={pdConsent} onChange={(e) => setPdConsent(e.target.checked)} />
                                <span>
                                    Даю <a href={PD_CONSENT_URL} target="_blank" rel="noopener noreferrer">согласие на обработку персональных данных</a> и ознакомлен(а) с <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">политикой обработки персональных данных</a>
                                </span>
                            </label>
                            {/* ст. 10.1 152-ФЗ: согласие на распространение оформляется отдельно от остальных */}
                            <label className="admin-login-consent jost">
                                <input type="checkbox" checked={pdDistributionConsent} onChange={(e) => setPdDistributionConsent(e.target.checked)} />
                                <span>
                                    Даю <a href={PD_DISTRIBUTION_CONSENT_URL} target="_blank" rel="noopener noreferrer">согласие на распространение персональных данных</a>, которые размещаю на своей публичной странице
                                </span>
                            </label>
                        </>
                    )}

                    {/* соглашение принимается кнопкой, а не чекбоксом: согласия на ПД по закону оформляются отдельно от других документов */}
                    {mode === 'register' && (
                        <span className="admin-login-consent admin-login-terms jost">
                            Нажимая «Зарегистрироваться», вы принимаете <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">пользовательское соглашение</a>
                        </span>
                    )}

                    {error && <Text size="xs" color="accent">{error}</Text>}

                    <Button type="submit" variant="solid" fullWidth textSize="m" disabled={loading || (mode === 'register' && (!pdConsent || !pdDistributionConsent))}>
                        {loading ? 'Подождите…' : mode === 'login' ? 'Войти' : 'Зарегистрироваться'}
                    </Button>

                    {mode === 'login' && (
                        <Button type="button" variant="ghost" fullWidth textSize="s" textColor="lightGray" onClick={() => window.location.assign('/reset-password')}>
                            Забыли пароль?
                        </Button>
                    )}
                </form>
            </div>
        </PageComponent>
    )
}
